/* eslint-disable no-nested-ternary */
import styled from '@emotion/styled';
import { forwardRef, SelectHTMLAttributes, useMemo } from 'react';

import DownIcon from '@/assets/icons/icon_down.svg';
import { mq } from '@/utility/mediaQuery';
import { defaultTheme } from '@/utility/theme';

export type Option = {
  value: string | number;
  displayName: string | number;
};

type Value = string | number;

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  width?: string;
  height?: string;
  isCenter?: boolean;
  options: Option[];
  disabledList?: Value[];
  isValid?: boolean;
  radius?: keyof typeof defaultTheme.border.radius;
  placeholder?: string;
}

const StyledSelect = styled.select(() =>
  mq({
    position: 'absolute',
    zIndex: '2',
    display: 'block',
    width: '100%',
    height: '100%',
    opacity: '0',
    appearance: 'none',
    cursor: 'pointer',
  }),
);

const SelectBox = styled.div(
  (props: {
    isCenter?: boolean;
    isValid?: boolean;
    radius?: keyof typeof defaultTheme.border.radius;
  }) =>
    mq({
      display: 'flex',
      justifyContent: props.isCenter ? 'center' : 'left',
      alignItems: 'center !important',
      width: '100%',
      height: '100%',
      padding: defaultTheme.spacing.l,
      fontSize: defaultTheme.font.size.s,
      backgroundColor: props.isValid
        ? defaultTheme.color.errorBg
        : defaultTheme.background.white,
      backgroundImage: `url(${DownIcon})`,
      backgroundPosition: 'right 16px center',
      backgroundSize: '12px',
      backgroundRepeat: 'no-repeat',
      border: props.isValid
        ? `1px solid ${defaultTheme.color.error}`
        : `1px solid ${defaultTheme.border.color.mediumGray}`,
      borderRadius: defaultTheme.border.radius[props.radius || 'xs'],
    }),
);

const StyledDiv = styled.div((props: { width?: string; height?: string }) =>
  mq({
    position: 'relative',
    height: props.height ? props.height : '40px',
    width: props.width ? props.width : '100px',
  }),
);

const Select = forwardRef<any, SelectProps>((props, ref) => {
  const {
    options,
    value,
    isValid,
    name,
    width,
    height,
    isCenter,
    radius,
    disabledList,
    autoComplete,
    placeholder,
    onChange,
  } = props;

  const selectedValue = (_val?: string | number | readonly string[]) =>
    useMemo(() => {
      const option = options.find(opt => opt.value === _val);
      if (option) {
        return option.displayName;
      }
      return placeholder || '-';
    }, [options, _val]);

  return (
    <StyledDiv width={width} height={height}>
      <StyledSelect
        name={name}
        onChange={onChange}
        value={value}
        autoComplete={autoComplete}
        ref={ref}
      >
        {options.length ? (
          options.map((val, index) => {
            return (
              <option
                data-testid="select-option"
                disabled={disabledList && disabledList.includes(val.value)}
                value={val.value}
                key={index}
              >
                {val.displayName}
              </option>
            );
          })
        ) : (
          <option value="">{placeholder || '-'}</option>
        )}
      </StyledSelect>
      <SelectBox isCenter={isCenter} isValid={isValid} radius={radius}>
        {selectedValue(value)}
      </SelectBox>
    </StyledDiv>
  );
});

export default Select;
